import { supabase } from "@/lib/supabase.ts";
import {
  SchemaTemplate,
  SchemaField,
  SchemaBuilderSession,
  ChatMessage,
} from "@/types/schemaBuilder";

export const schemaBuilderService = {
  // Get all templates for the current organization
  async getTemplates(): Promise<SchemaTemplate[]> {
    try {
      const { data, error } = await supabase
        .from("schema_templates")
        .select("*")
        .order("updated_at", { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase for frontend use
      return (data || []).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        schema: item.schema,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        organizationId: item.organization_id,
        createdBy: item.created_by,
      }));
    } catch (error) {
      console.error("Error fetching templates:", error);
      throw error;
    }
  },

  // Get a specific template by ID
  async getTemplateById(id: string): Promise<SchemaTemplate | null> {
    try {
      const { data, error } = await supabase
        .from("schema_templates")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (!data) return null;

      // Convert snake_case to camelCase for frontend use
      return {
        id: data.id,
        name: data.name,
        description: data.description,
        schema: data.schema,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        organizationId: data.organization_id,
        createdBy: data.created_by,
      };
    } catch (error) {
      console.error(`Error fetching template with ID ${id}:`, error);
      throw error;
    }
  },

  // Create a new template
  async createTemplate(
    template: Omit<SchemaTemplate, "id" | "createdAt" | "updatedAt">,
  ): Promise<SchemaTemplate> {
    try {
      // Convert camelCase to snake_case for database
      const dbTemplate = {
        name: template.name,
        description: template.description,
        schema: template.schema,
        organization_id: template.organizationId,
        created_by: template.createdBy,
      };

      const { data, error } = await supabase
        .from("schema_templates")
        .insert(dbTemplate)
        .select()
        .single();

      if (error) throw error;

      // Convert back to camelCase for frontend
      return {
        id: data.id,
        name: data.name,
        description: data.description,
        schema: data.schema,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        organizationId: data.organization_id,
        createdBy: data.created_by,
      };
    } catch (error) {
      console.error("Error creating template:", error);
      throw error;
    }
  },

  // Update an existing template
  async updateTemplate(
    id: string,
    template: Partial<SchemaTemplate>,
  ): Promise<SchemaTemplate> {
    try {
      // Convert camelCase to snake_case for database
      const dbTemplate: any = {};
      if (template.name !== undefined) dbTemplate.name = template.name;
      if (template.description !== undefined)
        dbTemplate.description = template.description;

      // If schema is provided, ensure field order is preserved
      if (template.schema !== undefined) {
        // Sort fields by their order property if it exists
        if (typeof template.schema === "object" && template.schema !== null) {
          const schemaEntries = Object.entries(template.schema);

          // Sort entries by order property if it exists
          schemaEntries.sort((a, b) => {
            const orderA = (a[1] as any).order !== undefined ? (a[1] as any).order : 999;
            const orderB = (b[1] as any).order !== undefined ? (b[1] as any).order : 999;
            return orderA - orderB;
          });

          // Rebuild schema object with sorted entries
          const sortedSchema = {};
          schemaEntries.forEach(([key, value]) => {
            sortedSchema[key] = value;
          });

          dbTemplate.schema = sortedSchema;
        } else {
          dbTemplate.schema = template.schema;
        }
      }

      if (template.organizationId !== undefined)
        dbTemplate.organization_id = template.organizationId;

      // Always update the updated_at timestamp
      dbTemplate.updated_at = new Date().toISOString();

      const { data, error } = await supabase
        .from("schema_templates")
        .update(dbTemplate)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // Convert back to camelCase for frontend
      return {
        id: data.id,
        name: data.name,
        description: data.description,
        schema: data.schema,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        organizationId: data.organization_id,
        createdBy: data.created_by,
      };
    } catch (error) {
      console.error(`Error updating template with ID ${id}:`, error);
      throw error;
    }
  },

  // Delete a template
  async deleteTemplate(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from("schema_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;
    } catch (error) {
      console.error(`Error deleting template with ID ${id}:`, error);
      throw error;
    }
  },

  // Create a new schema builder session
  async createSession(): Promise<SchemaBuilderSession> {
    try {
      const { data, error } = await supabase
        .from("schema_builder_sessions")
        .insert({
          messages: [],
          status: "active",
        })
        .select()
        .single();

      if (error) throw error;

      // Convert snake_case to camelCase for frontend use
      return {
        id: data.id,
        messages: data.messages || [],
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error("Error creating session:", error);
      throw error;
    }
  },

  // Add a message to a session
  async addMessage(
    sessionId: string,
    message: Omit<ChatMessage, "id" | "timestamp">,
  ): Promise<SchemaBuilderSession> {
    try {
      // First get the current session
      const { data: session, error: sessionError } = await supabase
        .from("schema_builder_sessions")
        .select("*")
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      // Add the new message
      const newMessage: ChatMessage = {
        id: crypto.randomUUID(),
        ...message,
        timestamp: new Date().toISOString(),
      };

      const updatedMessages = [...(session.messages || []), newMessage];

      // Update the session
      const { data, error } = await supabase
        .from("schema_builder_sessions")
        .update({
          messages: updatedMessages,
          updated_at: new Date().toISOString(),
        })
        .eq("id", sessionId)
        .select()
        .single();

      if (error) throw error;

      // Convert snake_case to camelCase for frontend use
      return {
        id: data.id,
        messages: data.messages || [],
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error) {
      console.error(`Error adding message to session ${sessionId}:`, error);
      throw error;
    }
  },

  // Process user message and generate schema
  async processMessage(
    sessionId: string,
    userMessage: string,
  ): Promise<{
    response: string;
    schema?: Record<string, SchemaField>;
  }> {
    // This would typically call an AI service to process the message
    // For now, we'll implement a simple rule-based system

    // Example implementation - in a real app, this would be much more sophisticated
    // and would likely use an external AI service
    const lowercaseMessage = userMessage.toLowerCase();

    let response = "I'm processing your request to build a schema.";
    let schema: Record<string, SchemaField> | undefined;

    if (
      lowercaseMessage.includes("create field") ||
      lowercaseMessage.includes("add field")
    ) {
      // Extract field name and type
      const nameMatch = userMessage.match(/field\s+([a-zA-Z0-9_]+)/i);
      const typeMatch = userMessage.match(/type\s+([a-zA-Z0-9_]+)/i);

      if (nameMatch && typeMatch) {
        const fieldName = nameMatch[1];
        const fieldType = typeMatch[1].toLowerCase() as
          | "string"
          | "number"
          | "boolean"
          | "object"
          | "array";

        // Create a basic field
        const field: SchemaField = {
          name: fieldName,
          type: fieldType,
          required: lowercaseMessage.includes("required"),
        };

        // Add validation if mentioned
        if (
          lowercaseMessage.includes("min length") ||
          lowercaseMessage.includes("minlength")
        ) {
          const minLengthMatch = userMessage.match(/min\s*length\s*(\d+)/i);
          if (minLengthMatch && field.type === "string") {
            field.validation = field.validation || {};
            field.validation.minLength = parseInt(minLengthMatch[1]);
          }
        }

        if (
          lowercaseMessage.includes("max length") ||
          lowercaseMessage.includes("maxlength")
        ) {
          const maxLengthMatch = userMessage.match(/max\s*length\s*(\d+)/i);
          if (maxLengthMatch && field.type === "string") {
            field.validation = field.validation || {};
            field.validation.maxLength = parseInt(maxLengthMatch[1]);
          }
        }

        schema = { [fieldName]: field };
        response = `I've added a ${fieldType} field named "${fieldName}".`;

        if (field.required) {
          response += " This field is required.";
        }

        if (field.validation) {
          if (field.validation.minLength) {
            response += ` It has a minimum length of ${field.validation.minLength}.`;
          }
          if (field.validation.maxLength) {
            response += ` It has a maximum length of ${field.validation.maxLength}.`;
          }
        }
      } else {
        response =
          "I couldn't understand the field details. Please specify the field name and type.";
      }
    } else if (
      lowercaseMessage.includes("nested object") ||
      lowercaseMessage.includes("add object")
    ) {
      // Handle nested object creation
      const nameMatch = userMessage.match(/object\s+([a-zA-Z0-9_]+)/i);

      if (nameMatch) {
        const objectName = nameMatch[1];

        schema = {
          [objectName]: {
            name: objectName,
            type: "object",
            properties: {},
          },
        };

        response = `I've added a nested object named "${objectName}". You can now add properties to this object.`;
      } else {
        response =
          "I couldn't understand the object details. Please specify the object name.";
      }
    } else if (
      lowercaseMessage.includes("array") ||
      lowercaseMessage.includes("list")
    ) {
      // Handle array creation
      const nameMatch =
        userMessage.match(/array\s+([a-zA-Z0-9_]+)/i) ||
        userMessage.match(/list\s+([a-zA-Z0-9_]+)/i);
      const typeMatch = userMessage.match(/of\s+([a-zA-Z0-9_]+)/i);

      if (nameMatch && typeMatch) {
        const arrayName = nameMatch[1];
        const itemType = typeMatch[1].toLowerCase() as
          | "string"
          | "number"
          | "boolean"
          | "object";

        schema = {
          [arrayName]: {
            name: arrayName,
            type: "array",
            items: {
              name: `${arrayName}Item`,
              type: itemType,
            },
          },
        };

        response = `I've added an array named "${arrayName}" with items of type "${itemType}".`;
      } else {
        response =
          "I couldn't understand the array details. Please specify the array name and item type.";
      }
    } else {
      response =
        "I'm not sure what you want to do. You can create fields, nested objects, or arrays. For example, 'Create a field name of type string' or 'Add an array users of object'.";
    }

    // In a real implementation, we would update the session with the new schema
    // and add the assistant's response as a message

    return { response, schema };
  },
};
